import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { dislikeContent, likeContent } from './apiService';
import { useUser } from './CacheProvider';
import { __t } from './i18n';
import { IconThumbsUp, IconThumbsUpFilled, IconThumbsDown, IconThumbsDownFilled } from './icons';
import { Box, Flex, Icon, IconButton } from './styles';
import { InputSize } from './theme';
import Tooltip from './Tooltip';
import useConditionalProps from './utils/useConditionalProps';
import { SpacingProps } from './withStyleSystem';

function Reaction({ id, type = 'content', flag = 'none', inputSize = 'default', round, loading: loadingProps }: ReactionProps) {
  const router = useRouter();
  const loginUrl = `/login?redirect=${router.asPath}`;
  const { loaded, authorized } = useUser();

  const value = useMemo(() => getValue(flag), [flag]);
  const [state, setState] = useState(value);
  const [loading, setLoading] = useState(false);
  const contentId = useConditionalProps((s) => s.contentId);
  const contentLongId = useConditionalProps((s) => s.contentLongId);
  const reaction = useConditionalProps((s) => s.reaction);

  useEffect(() => {
    setState(value);
  }, [value]);
  const updateMainContentReaction = useConditionalProps((s) => s.updateMainContentReaction);

  const handleClick = async (action: 'like' | 'dislike') => {
    if (!loaded) return;
    if (!authorized) return router.push(loginUrl);
    setLoading(true);

    const method = methods[type][action];
    method(id)
      .then(() => {
        setState(action);
        setLoading(false);
        if (id === contentId || id === contentLongId || id === router?.query?.id) {
          updateMainContentReaction(action);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const checkButtonActive = (action) => {
    if (id === contentId || id === contentLongId || id === router?.query?.id) {
      return reaction === action;
    } return state === action;
  };

  return (
    <Flex>
      {['like', 'dislike'].map((action, idx) => (
        <ReactionButton
          key={action}
          type={action as ReactionType}
          active={checkButtonActive(action)}
          onClick={handleClick}
          isLoading={!loaded || loading || !!loadingProps}
          inputSize={inputSize}
          round={round}
          ml={!idx ? '.5em' : undefined}
        />
      ))}
    </Flex>
  );
}

export const LikeButton = (props: SingleActionButtonProps) => <ReactionButton {...props} type="like" />;
export const DislikeButton = (props: SingleActionButtonProps) => <ReactionButton {...props} type="dislike" />;

const ReactionButton = ({
  type,
  active,
  isLoading,
  onClick,
  inputSize,
  round,
  count,
  ...spacingProps
}: ReactionButtonProps & SpacingProps) => {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);

  return (
    <Flex align="flex-end" {...spacingProps}>
      <StyledButton
        onClick={() => !isLoading && onClick(type)}
        isLoading={isLoading}
        active={active}
        round={round}
        inputSize={inputSize}
        ref={setReferenceElement}
        type={type as any}
        aria-label={__t(`common.${type}`)}
      >
        <ActionIcon type={type} active={active} round={round} />
      </StyledButton>
      {!!count && <Count value={count} isLoading={isLoading} />}
      {!isLoading && (
        <Tooltip anchor={referenceElement} placement="top">
          {__t(`common.${type}`)}
        </Tooltip>
      )}
    </Flex>
  );
};

const ActionIcon = ({ type, active, round }: Pick<ReactionButtonProps, 'type' | 'active' | 'round'>) => {
  const iconColor = () => {
    if (active && !round) {
      if (type === 'dislike') return 'warning';
      if (type === 'like') return 'success';
    }
    return undefined;
  };
  return <Icon size="inherit" color={iconColor()} cursor='pointer'>
    {actionIconMap[type][active && !round ? 'filled' : 'default']}
  </Icon>;
};

const Count = ({ value, isLoading, ...spacing }: { value?: number; isLoading?: boolean } & SpacingProps) => (
  <Box {...spacing} style={{ opacity: isLoading ? 0.5 : undefined }}>
    {value || ''}
  </Box>
);

const StyledButton = styled(IconButton)<Partial<ReactionButtonProps>>(({ theme, round, active, inputSize, isLoading, type }) => {
  let bg: string | undefined;
  if (round && active) bg = type === 'like' ? theme.palette.success[900][0] : theme.palette.warning[900][0];

  return {
    ...theme.font.responsiveSize(inputSize === 'small' ? 3 : 4),
    cursor: isLoading ? 'default' : 'pointer',
    padding: round ? '.45em' : '.25em',
    backgroundColor: bg,
    borderRadius: theme.radius.pill,
    opacity: isLoading ? 0.5 : undefined,
    transition: 'opacity .2s ease, background-color .3s ease',
    // iOS keeps :hover on the tapped element (and doesn't always focus it), so the
    // inherited hover/active/focus background sticks and hides the toggled color.
    // Pin those states to the active background, and only paint the real hover
    // background on devices that can actually hover.
    '&:hover, &:active, &:focus': {
      backgroundColor: bg ?? 'transparent',
    },
    '@media (hover: hover)': {
      '&:hover': {
        backgroundColor: bg ?? theme.palette.background[400][0],
      },
    },
  };
});

// ----- //
// UTILS //
// ----- //

const methods = {
  content: {
    like: likeContent,
    dislike: dislikeContent,
  },
};

const actionIconMap = {
  like: {
    default: <IconThumbsUp />,
    filled: <IconThumbsUpFilled />,
  },
  dislike: {
    default: <IconThumbsDown />,
    filled: <IconThumbsDownFilled />,
  },
};

function getValue(flag: string): ReactionType | 'none' {
  if (flag.includes('dislike')) return 'dislike';
  if (flag.includes('like')) return 'like';
  return 'none';
}

// ----- //
// TYPES //
// ----- //

export interface ReactionProps {
  id: string;
  type?: keyof typeof methods;
  flag?: string;
  inputSize?: InputSize;
  round?: boolean;
  loading?: boolean;
}

export type ReactionType = 'like' | 'dislike';

interface ReactionButtonProps {
  type: ReactionType;
  active?: boolean;
  isLoading?: boolean;
  inputSize?: ReactionProps['inputSize'];
  round?: ReactionProps['round'];
  count?: number;
  onClick: (action: ReactionType) => void;
}

type SingleActionButtonProps = Omit<ReactionButtonProps, 'type'> & SpacingProps;

export default Reaction;
